// Reusable inline-action modal for the Control Tower and other dashboards.
// Modes: "approve" | "reject" | "cancel" | "trips" | "waybill"
// API endpoints are the same ones Activities.jsx already calls.

import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { num } from "../lib/format";
import { Truck as TruckIcon, X } from "@phosphor-icons/react";
import { ReconcileCorrectionNote } from "./ReconcileCorrectionNote";

export default function ActivityActionsModal({ activity, mode, onClose, onDone, gps }) {
  // --- shared state ---
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  // --- approve mode ---
  const [approveQty, setApproveQty] = useState("");
  const [approvePrice, setApprovePrice] = useState("");
  const [approveLpt, setApproveLpt] = useState("");
  const [approveSource, setApproveSource] = useState("yard");
  const [topupKind, setTopupKind] = useState("station_fill");  // "station_fill" | "cash_purchase" — accounting tag on the non-yard portion
  const [yardLitres, setYardLitres] = useState("");   // for MIXED: explicit yard-side portion (top-up entered separately below)
  const [topupLitres, setTopupLitres] = useState("");
  const [topupPrice, setTopupPrice] = useState("");
  const [topupCurrency, setTopupCurrency] = useState("GHS");   // "GHS" | "XOF" — currency at the top-up station
  const [truckCredit, setTruckCredit] = useState(0);
  // --- reject / cancel mode ---
  const [reason, setReason] = useState("");
  // --- trips reconciliation ---
  const [tripsValue, setTripsValue] = useState("1");
  const [tonnesActualValue, setTonnesActualValue] = useState("");
  const [waitingActualValue, setWaitingActualValue] = useState("");
  const [leftoverValue, setLeftoverValue] = useState("");
  const [completedAtValue, setCompletedAtValue] = useState("");
  const [closeNotesValue, setCloseNotesValue] = useState("");
  const [leftoverEdited, setLeftoverEdited] = useState(false);
  // L/TRIP correction — normally snapshotted at request time, but if it was wrong
  // (e.g. ARKAS trips saved at 50 L/trip when it should be 10) the reconciler can
  // correct it here. Recomputes the cap live.
  const [tripsLpt, setTripsLpt] = useState("");
  const [tripsLptEdited, setTripsLptEdited] = useState(false);
  // Actual fuel used on this trip (litres). Optional — populated at reconcile time when the
  // burn was different from what was issued. Feeds cost-per-litre analytics + the card display.
  const [fuelActualValue, setFuelActualValue] = useState("");
  const [fuelActualEdited, setFuelActualEdited] = useState(false);

  // Initialise state per-activity / per-mode (mirrors Activities.jsx logic)
  useEffect(() => {
    if (!activity) return;
    setErr("");
    if (mode === "approve") {
      setApproveQty(String(activity.fuel_requested_l || ""));
      setApprovePrice("");
      setApproveLpt(String(activity.litres_per_trip && activity.litres_per_trip > 10
        ? activity.litres_per_trip
        : (activity.fuel_requested_l || 500)));
      setApproveSource("yard");
      setTopupKind("station_fill");
      setYardLitres("");
      setTopupLitres("");
      setTopupPrice("");
      // Auto-pick top-up currency from the truck's country: XOF for Togo/Benin, else GHS.
      const c = (activity.country || "GH").toUpperCase();
      setTopupCurrency(c === "TG" || c === "BJ" ? "XOF" : "GHS");
      setTruckCredit(0);
      if (activity.vehicle_number) {
        api.get("/jobs/fuel-credits")
          .then(({ data }) => {
            const row = (data || []).find((r) => r.vehicle_number === activity.vehicle_number);
            setTruckCredit(row?.credit_l || 0);
          })
          .catch(() => setTruckCredit(0));
      }
    } else if (mode === "reject" || mode === "cancel") {
      setReason("");
    } else if (mode === "trips") {
      setTripsValue(String(activity.trips_done || 1));
      setTonnesActualValue(activity.tonnes_actual ? String(activity.tonnes_actual) : (activity.tonnes_estimated ? String(activity.tonnes_estimated) : ""));
      setWaitingActualValue(activity.waiting_days_actual ? String(activity.waiting_days_actual) : (activity.waiting_days_estimated ? String(activity.waiting_days_estimated) : ""));
      setLeftoverValue("");
      setLeftoverEdited(false);
      setTripsLpt(String(activity.litres_per_trip || 10));
      setTripsLptEdited(false);
      // Pre-fill actual-fuel from previous entry, else leave blank (operator opts in).
      setFuelActualValue(activity.fuel_actual_l != null ? String(activity.fuel_actual_l) : "");
      setFuelActualEdited(false);
      setCloseNotesValue(activity.close_notes || "");
      // Default "Completed at" to local now formatted for <input type="datetime-local">
      const d = new Date();
      const localISO = new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
      setCompletedAtValue(localISO);
    }
  }, [activity, mode]);

  if (!activity) return null;

  const finish = async () => {
    onDone && (await onDone());
    onClose && onClose();
  };

  const doApprove = async () => {
    setErr("");
    // MIXED mode: total = yard + top-up (both editable side-by-side). All other modes use the single approveQty field.
    let qty;
    if (approveSource === "mixed") {
      const yl = parseFloat(yardLitres) || 0;
      const tl = parseFloat(topupLitres) || 0;
      qty = yl + tl;
      if (qty <= 0) { setErr("Enter yard litres and/or top-up litres."); return; }
      if (yl <= 0 || tl <= 0) { setErr("For Mixed, both yard and top-up litres must be greater than zero. Use 'Yard tank' or 'Station top-up' if one side is empty."); return; }
    } else {
      qty = parseFloat(approveQty);
      if (!qty || qty <= 0) { setErr("Approved litres must be greater than zero."); return; }
    }
    setBusy(true);
    try {
      const payload = { fuel_approved_l: qty, fuel_source: approveSource };
      if (approvePrice) payload.fuel_unit_price = parseFloat(approvePrice);
      const lpt = parseFloat(approveLpt);
      if (lpt && lpt > 0) payload.litres_per_trip = lpt;
      if (approveSource !== "yard") {
        const tl = parseFloat(topupLitres);
        const tp = parseFloat(topupPrice);
        if (!tl || tl <= 0) { setErr("Top-up litres must be greater than zero."); setBusy(false); return; }
        if (!tp || tp <= 0) { setErr("Top-up price/L must be greater than zero."); setBusy(false); return; }
        if (tl > qty + 0.001) { setErr(`Top-up (${tl} L) cannot exceed total (${qty} L).`); setBusy(false); return; }
        payload.topup_litres = tl;
        payload.topup_price = tp;
        payload.topup_currency = topupCurrency || "GHS";
        payload.topup_kind = topupKind;
      }
      await api.post(`/activities/${activity.id}/approve`, payload);
      await finish();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const doReject = async () => {
    setErr("");
    if (!reason.trim()) { setErr("Please give a reason."); return; }
    setBusy(true);
    try {
      await api.post(`/activities/${activity.id}/reject`, { reason: reason.trim() });
      await finish();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const doCancel = async () => {
    setErr("");
    if (!reason.trim()) { setErr("Please give a reason."); return; }
    setBusy(true);
    try {
      await api.post(`/activities/${activity.id}/cancel`, { reason: reason.trim() });
      await finish();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const doTrips = async () => {
    setErr("");
    const n = parseInt(tripsValue, 10);
    if (isNaN(n) || n < 0) { setErr("Trips must be 0 or more (use 0 for aborted)."); return; }
    setBusy(true);
    try {
      const payload = { trips_done: n };
      if (tonnesActualValue !== "") payload.tonnes_actual = parseFloat(tonnesActualValue);
      if (waitingActualValue !== "") payload.waiting_days_actual = parseFloat(waitingActualValue);
      if (leftoverEdited && leftoverValue !== "") payload.leftover_l = parseFloat(leftoverValue);
      if (tripsLptEdited && tripsLpt !== "") {
        const lpt = parseFloat(tripsLpt);
        if (!isNaN(lpt) && lpt > 0) payload.litres_per_trip = lpt;
      }
      if (fuelActualEdited && fuelActualValue !== "") {
        const fa = parseFloat(fuelActualValue);
        if (!isNaN(fa) && fa >= 0) payload.fuel_actual_l = fa;
      }
      if (completedAtValue) {
        // Convert local datetime-local back to ISO with timezone
        payload.completed_at = new Date(completedAtValue).toISOString();
      }
      if (closeNotesValue !== "") payload.close_notes = closeNotesValue.trim();
      await api.patch(`/activities/${activity.id}/trips-done`, payload);
      await finish();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const doWaybill = async () => {
    setErr("");
    setBusy(true);
    try {
      await api.post(`/activities/${activity.id}/waybill-received`);
      await finish();
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const titleByMode = {
    approve: "Approve & issue fuel",
    reject: "Reject request",
    cancel: "Cancel activity",
    trips: activity.status === "completed" ? "Correct trip count" : "Reconcile trips",
    waybill: "Mark waybill received",
  };

  return (
    <div className="fixed inset-0 z-[1200] bg-slate-900/60 grid place-items-center p-4" onClick={onClose} data-testid="activity-actions-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="p-5 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500">{activity.client || "—"}</div>
            <h3 className="font-heading font-black text-xl tracking-tight">{titleByMode[mode] || "Action"}</h3>
            <div className="text-xs text-slate-500 mt-1 flex items-center gap-2">
              <TruckIcon size={12} /> {activity.vehicle_number}
              <span>·</span>
              <span>requested {num(activity.fuel_requested_l)} L</span>
            </div>
          </div>
          <button onClick={onClose} data-testid="aam-close" className="text-slate-400 hover:text-ps-deep p-1">
            <X size={16} weight="bold" />
          </button>
        </div>

        <div className="p-5 space-y-3 max-h-[70vh] overflow-y-auto">
          {/* APPROVE */}
          {mode === "approve" && (
            <>
              {(() => {
                // Show the leftover-credit hint using the effective total (mixed = yard+topup, else = approveQty)
                if (!(truckCredit > 0)) return null;
                const q = approveSource === "mixed"
                  ? (parseFloat(yardLitres) || 0) + (parseFloat(topupLitres) || 0)
                  : (parseFloat(approveQty) || 0);
                const will = Math.min(q, truckCredit);
                const fromYard = Math.max(0, q - will);
                return (
                  <div className="bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-900 rounded" data-testid="aam-credit-banner">
                    <div className="font-semibold">
                      <span className="font-mono-data">{num(truckCredit, 1)} L</span> leftover fuel on truck {activity.vehicle_number}
                    </div>
                    {q > 0 && (
                      <div className="text-amber-800 mt-0.5">
                        Will auto-deduct <span className="font-semibold font-mono-data">{num(will, 1)} L</span> from credit · only <span className="font-semibold font-mono-data">{num(fromYard, 1)} L</span> pulled from yard.
                      </div>
                    )}
                  </div>
                );
              })()}
              {approveSource !== "mixed" && (
                <Field label="Approved litres *" type="number" step="1" min="0" testid="aam-approve-qty" value={approveQty} onChange={setApproveQty} />
              )}

              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Fuel source</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="aam-source-toggle">
                  {[
                    { v: "yard",  label: "Yard tank",     hint: "Deducts from yard stock",   kind: null },
                    { v: "topup", label: "Station top-up", hint: "Bought at a filling station", kind: "station_fill" },
                    { v: "topup", label: "Cash purchase",  hint: "Opportunistic cash buy",     kind: "cash_purchase" },
                    { v: "mixed", label: "Mixed",          hint: "Part yard + non-yard",       kind: null },
                  ].map((o, i) => {
                    const active = approveSource === o.v && (o.kind === null || topupKind === o.kind);
                    const onPick = () => {
                      setApproveSource(o.v);
                      if (o.kind) setTopupKind(o.kind);
                    };
                    return (
                      <button key={`${o.v}-${o.kind || "any"}-${i}`} type="button" onClick={onPick} data-testid={`aam-source-${o.kind === "cash_purchase" ? "cash" : o.v}`}
                        className={`text-left border rounded-md px-2 py-1.5 text-xs transition-colors ${active ? "border-ps-blue bg-sky-50 text-ps-deep" : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"}`}>
                        <div className="font-semibold flex items-center gap-1.5">
                          <span className={`w-1.5 h-1.5 rounded-full ${active ? "bg-ps-blue" : "bg-slate-300"}`} />
                          {o.label}
                        </div>
                        <div className="text-[9px] text-slate-500 mt-0.5">{o.hint}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              {approveSource === "mixed" && (
                <div className="space-y-2 border-l-2 border-ps-blue/40 bg-sky-50/50 px-3 py-2 rounded" data-testid="aam-mixed-block">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-ps-blue font-semibold">Yard + {topupKind === "cash_purchase" ? "cash purchase" : "station"} split</div>
                    <div className="flex gap-1" data-testid="aam-mixed-topup-kind">
                      {[
                        { v: "station_fill",  label: "Station" },
                        { v: "cash_purchase", label: "Cash" },
                      ].map((k) => (
                        <button key={k.v} type="button" onClick={() => setTopupKind(k.v)} data-testid={`aam-mixed-kind-${k.v === "cash_purchase" ? "cash" : "station"}`}
                          className={`px-2 py-0.5 text-[10px] font-semibold rounded border transition-colors ${topupKind === k.v ? "border-ps-blue bg-white text-ps-deep" : "border-slate-200 bg-slate-50 text-slate-500 hover:bg-white"}`}>
                          {k.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label="Yard litres *" type="number" step="0.1" min="0" testid="aam-yard-litres" value={yardLitres} onChange={setYardLitres} />
                    <Field label={`${topupKind === "cash_purchase" ? "Cash" : "Top-up"} litres *`} type="number" step="0.1" min="0" testid="aam-mixed-topup-litres" value={topupLitres} onChange={setTopupLitres} />
                  </div>
                  {(() => {
                    const yl = parseFloat(yardLitres) || 0;
                    const tl = parseFloat(topupLitres) || 0;
                    const total = yl + tl;
                    return total > 0 ? (
                      <div className="text-[11px] text-ps-deep mt-1" data-testid="aam-mixed-total">
                        → Total: <span className="font-mono-data font-bold">{num(total, 1)} L</span> <span className="text-slate-500">({num(yl, 1)} yard + {num(tl, 1)} {topupKind === "cash_purchase" ? "cash" : "top-up"})</span>
                      </div>
                    ) : (
                      <div className="text-[11px] text-slate-500 mt-1">Enter yard and {topupKind === "cash_purchase" ? "cash" : "top-up"} litres — total is auto-computed.</div>
                    );
                  })()}
                  <div className="grid grid-cols-[1fr_auto] gap-2 items-end">
                    <Field label={`${topupKind === "cash_purchase" ? "Cash" : "Top-up"} price / L (${topupCurrency}) *`} type="number" step="0.01" min="0" testid="aam-topup-price" value={topupPrice} onChange={setTopupPrice} />
                    <CurrencyToggle value={topupCurrency} onChange={setTopupCurrency} testid="aam-topup-currency-mixed" />
                  </div>
                </div>
              )}
              {approveSource === "topup" && (
                <div className={`space-y-2 border-l-2 px-3 py-2 rounded ${topupKind === "cash_purchase" ? "border-emerald-400/60 bg-emerald-50/40" : "border-ps-orange/40 bg-amber-50/40"}`} data-testid="aam-topup-block">
                  <div className={`text-[10px] uppercase tracking-[0.25em] font-semibold ${topupKind === "cash_purchase" ? "text-emerald-700" : "text-ps-orange"}`}>
                    {topupKind === "cash_purchase" ? "Cash purchase" : "Station top-up"}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Field label={`${topupKind === "cash_purchase" ? "Cash" : "Top-up"} litres`} type="number" step="0.1" min="0" testid="aam-topup-litres" value={topupLitres} onChange={setTopupLitres} />
                    <Field label={`Price / L (${topupCurrency})`} type="number" step="0.01" min="0" testid="aam-topup-price" value={topupPrice} onChange={setTopupPrice} />
                  </div>
                  <div className="flex justify-end">
                    <CurrencyToggle value={topupCurrency} onChange={setTopupCurrency} testid="aam-topup-currency" />
                  </div>
                </div>
              )}
              <Field label="Yard unit price (GHS / L) — blank = last recorded" type="number" step="0.01" min="0" testid="aam-approve-price" value={approvePrice} onChange={setApprovePrice} />
              <div>
                <Field label="Litres per trip (caps trip count)" type="number" step="1" min="1" testid="aam-approve-lpt" value={approveLpt} onChange={setApproveLpt} />
                {(() => {
                  const q = approveSource === "mixed"
                    ? (parseFloat(yardLitres) || 0) + (parseFloat(topupLitres) || 0)
                    : (parseFloat(approveQty) || 0);
                  const lpt = parseFloat(approveLpt) || 10;
                  const planned = Math.max(1, Math.floor(q / Math.max(lpt, 0.0001)));
                  return q > 0 ? (
                    <div className="text-[11px] text-slate-600 mt-1" data-testid="aam-trip-cap">
                      → cap of <span className="font-semibold text-ps-deep">{planned} trip{planned === 1 ? "" : "s"}</span>.
                    </div>
                  ) : null;
                })()}
              </div>
            </>
          )}

          {/* REJECT / CANCEL */}
          {(mode === "reject" || mode === "cancel") && (
            <div>
              <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Reason *</label>
              <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="aam-reason" className="w-full border border-slate-300 px-3 py-2 text-sm" placeholder={mode === "reject" ? "Why is this fuel request being rejected?" : "Why cancel this activity?"} />
            </div>
          )}

          {/* TRIPS RECONCILE */}
          {mode === "trips" && (
            <>
              <ReconcileCorrectionNote activity={activity} />
              {gps && (
                <div className="bg-emerald-50 border border-emerald-200 rounded p-2.5 text-[11px] flex items-center gap-2" data-testid="aam-trips-gps">
                  <span className={`w-1.5 h-1.5 rounded-full ${gps.online ? "bg-emerald-500 animate-pulse" : "bg-slate-400"}`} />
                  <span className="font-semibold uppercase tracking-wider text-emerald-800">GPS</span>
                  {typeof gps.speed_kmh === "number" && <span className="font-mono-data text-slate-600">{gps.speed_kmh.toFixed?.(0) ?? gps.speed_kmh} km/h</span>}
                  <span className="text-slate-700 truncate flex-1" title={gps.address}>{gps.address || "—"}</span>
                  {gps.last_seen && <span className="text-slate-500 shrink-0">{String(gps.last_seen).slice(11, 16)}</span>}
                </div>
              )}
              {(() => {
                const issued = parseFloat(activity.fuel_issued_l || activity.fuel_approved_l || 0);
                const lptNum = parseFloat(tripsLpt) || parseFloat(activity.litres_per_trip) || 10;
                const liveCap = lptNum > 0 ? Math.max(1, Math.floor(issued / lptNum)) : 1;
                return (
                  <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs grid grid-cols-3 gap-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Fuel issued</div>
                      <div className="font-mono-data text-sm font-semibold text-ps-deep">{num(issued)} L</div>
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center justify-between">
                        <span>L / trip</span>
                        {tripsLptEdited && (
                          <button
                            type="button"
                            onClick={() => { setTripsLpt(String(activity.litres_per_trip || 10)); setTripsLptEdited(false); }}
                            data-testid="aam-trips-lpt-reset"
                            className="text-[9px] text-amber-700 hover:underline normal-case tracking-normal"
                          >
                            reset
                          </button>
                        )}
                      </div>
                      <input
                        type="number" min="0.1" step="0.1"
                        value={tripsLpt}
                        onChange={(e) => { setTripsLpt(e.target.value); setTripsLptEdited(true); }}
                        data-testid="aam-trips-lpt"
                        title="How many litres the truck typically uses per trip on this contract. Adjust if the value snapshotted at request time was wrong."
                        className={`w-full mt-0.5 border px-2 py-1 text-sm font-mono-data tabular font-semibold text-ps-deep focus:border-ps-blue rounded ${tripsLptEdited ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white"}`}
                      />
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-500">Cap</div>
                      <div className="font-mono-data text-sm font-semibold text-lime-700" data-testid="aam-trips-cap">{liveCap} trip{liveCap === 1 ? "" : "s"}</div>
                    </div>
                  </div>
                );
              })()}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500">Trips actually completed</label>
                  <button
                    type="button"
                    onClick={() => {
                      setTripsValue("0");
                      const issued = parseFloat(activity.fuel_issued_l || activity.fuel_approved_l || 0);
                      setLeftoverValue(issued.toFixed(2));
                      setLeftoverEdited(true);
                    }}
                    data-testid="aam-trips-aborted"
                    className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-300 text-amber-700 hover:bg-amber-50 font-semibold"
                  >
                    Aborted — 0 trips
                  </button>
                </div>
              {(() => {
                const issued = parseFloat(activity.fuel_issued_l || activity.fuel_approved_l || 0);
                const lptNum = parseFloat(tripsLpt) || parseFloat(activity.litres_per_trip) || 10;
                const liveCap = lptNum > 0 ? Math.max(1, Math.floor(issued / lptNum)) : 1;
                return (
                  <>
                    <input type="number" min="0" max={liveCap} step="1" value={tripsValue} onChange={(e) => setTripsValue(e.target.value)} data-testid="aam-trips-input" autoFocus className="w-full border border-slate-300 px-3 py-2 text-2xl font-mono-data tabular text-center font-bold text-ps-deep focus:border-ps-blue" />
                    <div className="text-[11px] text-slate-500 mt-1">Between 0 (aborted) and {liveCap} (fuel cap).</div>
                  </>
                );
              })()}
              </div>
              {/* Leftover / L-per-trip formula box */}
              {(() => {
                const issued = parseFloat(activity.fuel_issued_l || activity.fuel_approved_l || 0);
                const lpt = parseFloat(tripsLpt) || parseFloat(activity.litres_per_trip) || 10;
                const cap = lpt > 0 ? Math.max(1, Math.floor(issued / lpt)) : 1;
                const n = parseInt(tripsValue || 0, 10);
                const formula = Math.max(0, (cap - (isNaN(n) ? 0 : n)) * lpt);
                const shown = leftoverEdited ? leftoverValue : (formula > 0 ? formula.toFixed(2) : "0");
                return (
                  <div className="bg-amber-50/40 border border-amber-200 rounded p-3 space-y-1.5" data-testid="aam-trips-leftover-block">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] uppercase tracking-[0.25em] text-amber-900 font-semibold">Litres left in tank (credited to truck)</label>
                      {leftoverEdited && (
                        <button type="button" onClick={() => { setLeftoverEdited(false); setLeftoverValue(""); }} data-testid="aam-trips-leftover-reset" className="text-[10px] text-amber-700 hover:underline">reset to formula</button>
                      )}
                    </div>
                    <input
                      type="number" step="0.1" min="0"
                      value={shown}
                      onChange={(e) => { setLeftoverValue(e.target.value); setLeftoverEdited(true); }}
                      data-testid="aam-trips-leftover"
                      className="w-full border border-amber-300 px-3 py-2 text-lg font-mono-data tabular text-ps-deep focus:border-ps-blue bg-white"
                    />
                    <div className="text-[10px] text-amber-800">
                      Formula assumes {lpt} L/trip — override with the actual tank reading if you measured it.
                    </div>
                  </div>
                );
              })()}

              {/* Actual fuel used — optional. Populate when the burn was different from what was issued. */}
              {(() => {
                const issued = parseFloat(activity.fuel_issued_l || activity.fuel_approved_l || 0);
                const shown = fuelActualEdited ? fuelActualValue : (activity.fuel_actual_l != null ? String(activity.fuel_actual_l) : "");
                const parsed = parseFloat(shown);
                const delta = !isNaN(parsed) && shown !== "" ? parsed - issued : null;
                return (
                  <div className="bg-sky-50/40 border border-sky-200 rounded p-3 space-y-1.5" data-testid="aam-trips-actual-block">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] uppercase tracking-[0.25em] text-ps-blue font-semibold">Actual fuel used (optional)</label>
                      {fuelActualEdited && (
                        <button type="button" onClick={() => { setFuelActualEdited(false); setFuelActualValue(activity.fuel_actual_l != null ? String(activity.fuel_actual_l) : ""); }} data-testid="aam-trips-actual-reset" className="text-[10px] text-ps-blue hover:underline">clear</button>
                      )}
                    </div>
                    <input
                      type="number" step="0.1" min="0"
                      value={shown}
                      onChange={(e) => { setFuelActualValue(e.target.value); setFuelActualEdited(true); }}
                      placeholder={`${issued} L issued`}
                      data-testid="aam-trips-actual"
                      className="w-full border border-sky-300 px-3 py-2 text-lg font-mono-data tabular text-ps-deep focus:border-ps-blue bg-white"
                    />
                    <div className="text-[10px] text-ps-blue/80">
                      {delta == null ? (
                        <>How many litres this trip actually burned. Leave blank if it matches what was issued.</>
                      ) : delta > 0 ? (
                        <>Truck burned <span className="font-mono-data font-bold">{delta.toFixed(1)} L</span> <strong>more</strong> than issued — recorded for cost-per-litre analytics.</>
                      ) : delta < 0 ? (
                        <>Truck burned <span className="font-mono-data font-bold">{Math.abs(delta).toFixed(1)} L</span> <strong>less</strong> than issued.</>
                      ) : (
                        <>Actual matches issued.</>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Completed at */}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Completed on (date &amp; time)</label>
                <input
                  type="datetime-local"
                  value={completedAtValue}
                  onChange={(e) => setCompletedAtValue(e.target.value)}
                  data-testid="aam-trips-completed-at"
                  className="w-full border border-slate-300 px-3 py-2 text-sm font-mono-data tabular focus:border-ps-blue"
                />
                <div className="text-[10px] text-slate-500 mt-1">Defaults to now — change it if you&apos;re reconciling yesterday&apos;s shift.</div>
              </div>

              {/* Closing notes */}
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">Closing notes (optional)</label>
                <textarea
                  rows={2}
                  value={closeNotesValue}
                  onChange={(e) => setCloseNotesValue(e.target.value)}
                  data-testid="aam-trips-notes"
                  placeholder="e.g. Last trip aborted due to flat tyre at Tema. Customer cancelled remaining trips."
                  className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-ps-blue"
                />
              </div>

              {(activity.rate_per_unit_snapshot > 0 || activity.waiting_day_rate_snapshot > 0) && (
                <div className="border-t border-slate-200 pt-3" data-testid="aam-trips-billing">
                  <div className="text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-2 font-semibold">Billing</div>
                  <div className="text-[11px] text-slate-600 mb-2">
                    Mode: <span className="font-semibold text-ps-deep">{activity.billing_mode === "per_tonne" ? "Per tonne" : "Per trip"}</span>
                    {activity.rate_per_unit_snapshot > 0 && (
                      <> · {activity.rate_per_unit_snapshot} {activity.rate_currency_snapshot} / {activity.billing_mode === "per_tonne" ? "t" : "trip"}</>
                    )}
                  </div>
                  {activity.billing_mode === "per_tonne" && (
                    <Field label="Tonnes actually carried" type="number" step="0.01" min="0" testid="aam-trips-tonnes" value={tonnesActualValue} onChange={setTonnesActualValue} placeholder={activity.tonnes_estimated ? `estimate ${activity.tonnes_estimated} t` : "0"} />
                  )}
                  {activity.waiting_day_rate_snapshot > 0 && (
                    <Field label="Waiting days" type="number" step="0.5" min="0" testid="aam-trips-wait" value={waitingActualValue} onChange={setWaitingActualValue} placeholder="0" />
                  )}
                  {(() => {
                    const rate = parseFloat(activity.rate_per_unit_snapshot) || 0;
                    const wdr = parseFloat(activity.waiting_day_rate_snapshot) || 0;
                    const qty = activity.billing_mode === "per_tonne" ? parseFloat(tonnesActualValue || 0) : parseInt(tripsValue || 0, 10);
                    const wd = parseFloat(waitingActualValue || 0);
                    const total = rate * qty + wdr * wd;
                    return total > 0 ? (
                      <div className="mt-2 bg-lime-50 border border-lime-200 rounded p-2 text-xs" data-testid="aam-trips-preview">
                        Billable: <span className="font-mono-data font-bold text-lime-700">{total.toFixed(2)} {activity.rate_currency_snapshot}</span>
                      </div>
                    ) : null;
                  })()}
                </div>
              )}
            </>
          )}

          {/* WAYBILL */}
          {mode === "waybill" && (
            <div className="text-sm text-slate-700">
              Mark the customer waybill (CMR) as received for <span className="font-semibold">{activity.client}</span> on truck <span className="font-mono-data font-semibold text-ps-blue">{activity.vehicle_number}</span>?
              <div className="text-xs text-slate-500 mt-2">This closes the awaiting-waybill state so the trip can be invoiced.</div>
            </div>
          )}

          {err && <div className="text-xs text-ps-red bg-red-50 border border-red-200 rounded p-2" data-testid="aam-error">{err}</div>}
        </div>

        <div className="px-5 py-4 bg-slate-50 border-t border-slate-200 flex gap-2">
          <button onClick={onClose} disabled={busy} className="flex-1 border border-slate-300 py-2.5 text-sm hover:bg-slate-100 rounded disabled:opacity-50">Close</button>
          {mode === "approve" && <button onClick={doApprove} disabled={busy} data-testid="aam-approve-confirm" className="flex-1 bg-ps-blue text-white py-2.5 text-sm font-semibold hover:bg-ps-deep rounded disabled:opacity-50">{busy ? "Issuing…" : "Issue fuel"}</button>}
          {mode === "reject"  && <button onClick={doReject}  disabled={busy} data-testid="aam-reject-confirm"  className="flex-1 bg-ps-red text-white py-2.5 text-sm font-semibold hover:bg-red-700 rounded disabled:opacity-50">{busy ? "…" : "Reject"}</button>}
          {mode === "cancel"  && <button onClick={doCancel}  disabled={busy} data-testid="aam-cancel-confirm"  className="flex-1 bg-slate-700 text-white py-2.5 text-sm font-semibold hover:bg-slate-800 rounded disabled:opacity-50">{busy ? "…" : "Cancel activity"}</button>}
          {mode === "trips"   && <button onClick={doTrips}   disabled={busy} data-testid="aam-trips-confirm"   className="flex-1 bg-ps-blue text-white py-2.5 text-sm font-semibold hover:bg-ps-deep rounded disabled:opacity-50">{busy ? "Saving…" : "Save"}</button>}
          {mode === "waybill" && <button onClick={doWaybill} disabled={busy} data-testid="aam-waybill-confirm" className="flex-1 bg-indigo-600 text-white py-2.5 text-sm font-semibold hover:bg-indigo-700 rounded disabled:opacity-50">{busy ? "…" : "Mark received"}</button>}
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, type = "text", value, onChange, ...rest }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.25em] text-slate-500 mb-1">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="w-full border border-slate-300 px-3 py-2 text-sm focus:border-ps-blue" {...rest} />
    </div>
  );
}

// Small pill toggle for choosing the currency at a station top-up (GHS vs XOF).
// XOF is used when the truck refuels in Togo / Benin — otherwise defaults to GHS.
function CurrencyToggle({ value, onChange, testid }) {
  return (
    <div className="inline-flex border border-slate-300 rounded overflow-hidden" data-testid={testid}>
      {["GHS", "XOF"].map((c) => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          data-testid={`${testid}-${c.toLowerCase()}`}
          className={`px-2.5 py-1.5 text-[11px] font-mono-data font-semibold tracking-wide ${value === c ? "bg-ps-blue text-white" : "bg-white text-slate-500 hover:bg-slate-50"}`}
        >
          {c}
        </button>
      ))}
    </div>
  );
}

